import { format } from 'date-fns';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { StateBadge } from '@/components/badge/state-badge';
import { useRemoveFromApp } from '@/components/engagements/use-remove-from-app';
import { useUnschedule } from '@/components/engagements/use-unschedule';
import { STATE_PLANNING } from '@/constants/version-state';
import { CreateEntryButton } from '@/components/create-entry-button';
import { RowCard } from '@/components/list/row-card';
import { ListEmpty, ListLoading } from '@/components/list/status-text';
import { ScheduleVersionDialog } from '@/components/prds/schedule-version-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useApp, useApps } from '@/hooks/apps';
import { useToast } from '@/hooks/use-toast';
import { mutateRequest, postFetcher } from '@/lib/swr';
import { applyScheduling, engagementVersion, engagementAppIds } from '@/lib/engagement';
import { versionLabel } from '@/lib/version';
import type { App, Engagement, Prd } from '@/types/domain';
import { EllipsisIcon } from 'lucide-react';

// Scheduling and App controls share the PRD business detail returned by the service.
const useLinkedEngagements = (prdId: string) => {
  const { data: prd, isLoading } = useSWR<Prd>([`/prds/${prdId}`]);
  const engagements = prd?.engagements;
  const linked = useMemo(
    () => (engagements ?? []).filter((engagement) => !!engagement.versionId),
    [engagements],
  );
  const linkedPids = useMemo(
    () => new Set(linked.map((engagement) => String(engagement.appId))),
    [linked],
  );
  return { linked, linkedPids, isLoading };
};

// App additions are a business action; the service creates the execution context.
const useAddApp = (prd: Prd) => {
  const { toast } = useToast();
  return useSWRMutation(`/prds/${prd.id}/apps`, postFetcher<Prd>, {
    onSuccess: (saved) => {
      applyScheduling(prd.id, saved);
      mutateRequest('/me/inbox');
    },
    onError: () => toast({ variant: 'destructive', title: "Failed to add app" }),
  });
};

// Context-App scheduling action in the Apps & versions section. Pool rows
// already offer scheduling; a planning version can also return to the pool.
export const AppSchedulingActions = ({
  prd,
  appId,
}: {
  prd: Prd;
  appId: string;
}) => {
  const { linked } = useLinkedEngagements(prd.id);
  const { unschedule, unscheduling } = useUnschedule((saved) => applyScheduling(prd.id, saved));

  const scheduled = linked.find(
    (engagement) => String(engagement.appId) === String(appId),
  );
  if (!scheduled || scheduled.versionState !== STATE_PLANNING) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={unscheduling}
      onClick={() => unschedule(scheduled.id)}
      title={`从 ${scheduled.versionName ?? ''} 下车,回该 App 需求池`}
    >
      放回需求池
    </Button>
  );
};

// Pick the apps the PRD relates to (each = a pool row until scheduled).
// Header form (default) is the primary CreateEntryButton; `compact` is the
// outline Section-action form used next to the Apps & versions title.
export const AddAppDropdown = ({
  prd,
  compact,
}: {
  prd: Prd;
  compact?: boolean;
}) => {
  const Apps: App[] = useApps();
  const { trigger: addApp, isMutating } = useAddApp(prd);
  const relatedIds = new Set(engagementAppIds(prd));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <CreateEntryButton
            label="Add"
            variant="outline"
            className="h-6 px-2 py-0 shadow-none"
          />
        ) : (
          <CreateEntryButton label="Apps" className="h-7" />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-60 w-[200px] overflow-y-auto"
      >
        <DropdownMenuLabel>Add app</DropdownMenuLabel>
        {Apps.map((app) => (
          <DropdownMenuItem
            key={app.code}
            disabled={isMutating || relatedIds.has(String(app.code))}
            onSelect={() => { addApp({ appId: String(app.code) }).catch(() => {}); }}
          >
            {app.code} - {app.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// A version the PRD is already scheduled into: state is interactive,
// linked date on the right. Two-line label (app above, version below) so the
// version number survives the narrow detail aside — a single "app - version"
// line gets its tail (the number) truncated first. No index/Task link here;
// both add noise at this width (the Feishu task stays on the version page).
const LinkedRow = ({ engagement }: { engagement: Engagement }) => {
  const version = engagementVersion(engagement);
  const apps = useApps();
  const navigate = useNavigate();
  const app = useApp(engagement.appId);
  if (!version) return null;
  return (
    <LinkedRowBody
      engagement={engagement}
      version={version}
      apps={apps}
      appName={app?.name}
      navigate={navigate}
    />
  );
};

const LinkedRowBody = ({
  engagement,
  version,
  apps,
  appName,
  navigate,
}: {
  engagement: Engagement;
  version: NonNullable<ReturnType<typeof engagementVersion>>;
  apps: App[];
  appName?: string;
  navigate: ReturnType<typeof useNavigate>;
}) => {
  const label = `${version.versionName ?? ''}${
    version.versionCode ? `(${version.versionCode})` : ''
  }`;
  return (
    // Lands on the version with this PRD's sheet open (WED-194).
    <RowCard
      onClick={() =>
        navigate(`/apps/${version.pid}/versions/${version.id}?engagement=${engagement.id}`)
      }
    >
      <div className="min-w-0 flex-1">
        {appName && (
          <p className="truncate text-xs text-muted-foreground">{appName}</p>
        )}
        <p
          title={versionLabel(version, apps)}
          className="truncate text-sm font-medium"
        >
          {label}
        </p>
      </div>
      {/* No priority here (owner 2026-09-08): this board answers "which apps /
          which versions", triage order belongs to the app pool and version pages. */}
      {/* State column: fixed width, left-aligned so badges of different
          label lengths line up across rows. Read-only — version state is
          managed on the version page, not from a PRD's board. */}
      <div className="flex w-24 shrink-0 items-center justify-start">
        <StateBadge state={version.state ?? 0} />
      </div>
      <span
        title="Linked at"
        className="w-12 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
      >
        {engagement.createdAt ? format(new Date(engagement.createdAt), 'MMM d') : ''}
      </span>
    </RowCard>
  );
};

// A pool row supports scheduling and removal from this App.
const PendingRow = ({
  prd,
  appId,
  readOnly,
}: {
  prd: Prd;
  appId: string | number;
  /** Fallback gate when the row carries no canManage (older payloads). */
  readOnly?: boolean;
}) => {
  const app = useApp(appId);
  const navigate = useNavigate();
  // Pool engagement row (versionId NULL) — /prds/:id hydrates engagements,
  // which the scheduled-only /engagements?prdId cache doesn't carry.
  const engagement = (prd.engagements ?? []).find(
    (e) => String(e.appId) === String(appId),
  );
  const manageable = engagement?.canManage ?? !readOnly;
  const { remove, isMutating: removing } = useRemoveFromApp(
    engagement?.id ?? '',
    prd.id,
  );
  // Whole row → that app's pool (RowCard skips clicks on the inner
  // schedule button / overflow menu); linked rows go to their version.
  return (
    <RowCard onClick={() => navigate(`/apps/${appId}/wish-pool`)}>
      <span className="min-w-0 flex-1 truncate font-medium">
        {app?.name ?? appId}
      </span>
      {/* Schedule action sits in the state column so columns line up with
          the linked rows; the date slot holds the overflow menu. */}
      <div className="flex w-24 shrink-0 items-center justify-start">
        <ScheduleVersionDialog prd={prd} pid={String(appId)} />
      </div>
      <div className="flex w-16 shrink-0 items-center justify-end">
        {manageable && (prd.engagements?.length ?? 0) > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" aria-label="App actions" className="h-6 w-6 p-0">
                <EllipsisIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={removing}
                onClick={() => { remove().catch(() => {}); }}
                className="text-destructive"
              >
                Remove from app
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </RowCard>
  );
};

// Merged pool × linked-versions view: waiting apps first, then one row
// per scheduled version (state shown).
export const ScheduleBoard = ({
  prd,
  readOnly,
}: {
  prd: Prd;
  readOnly?: boolean;
}) => {
  const apps: App[] = useApps();
  const { linked, linkedPids, isLoading } = useLinkedEngagements(prd.id);

  // Apps outside the user's visibility don't show here at all (owner
  // 2026-08-18) — matching the /engagements channel's server-side filter.
  const visibleIds = new Set(apps.map((a) => String(a.id)));
  const pendingApps = engagementAppIds(prd)
    .filter((id) => !linkedPids.has(id) && visibleIds.has(id))
    .map((id) => ({ id }));

  if (isLoading) return <ListLoading />;
  if (linked.length === 0 && pendingApps.length === 0)
    return <ListEmpty>No related apps — add one to schedule.</ListEmpty>;

  // Waiting apps first — they're the ones needing action.
  return (
    <div className="flex flex-col gap-1">
      {pendingApps.map((w) => (
        <PendingRow key={w.id} prd={prd} appId={w.id} readOnly={readOnly} />
      ))}
      {linked.map((engagement) => (
        <LinkedRow key={engagement.id} engagement={engagement} />
      ))}
    </div>
  );
};

export default ScheduleBoard;
